import { type JSX } from 'react';
import {
  Lens,
  over,
  append,
  type,
  assocPath,
  path,
  dissocPath,
  assoc,
  omit,
  init,
  clone,
  takeLast,
  take,
  mergeDeepWithKey,
  update,
  lensPath,
  set,
  forEachObjIndexed,
  last,
  has,
  intersperse,
  mergeDeepRight,
} from 'ramda';
import { IDefinedBusinessDataFormContext } from '../BusinessDataForm';
import { IBusinessDataFormState } from '../BusinessDataForm';
import { INodeData } from '../../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import { AvenyCollectionMap } from '../../../../../../../Model/ModelContext/ModelContext';
import { IClientBusinessModel } from '../../../../../../../Types/ClientModelTypes';
import businessDataFormHasChanged from '../BusinessDataFormHasChanged/businessDataFormHasChanged';
import {
  getAssociationIdVersion,
  isValidInClass,
} from '../../../../../../../Model/avenyModelUtils';
import { IconType } from '../../../../../../../BaseComponents/Icon/Utils/IconDefinitions';
import { isObject, isArray } from '../../../../../../../Utils/ramdaCookbook';
import { mergeBusinessDataMutations } from './MergeBusinessDataMutations';
import { TSelectedDetail } from '../../../../../../../Types/MasterDetailTypes';
import { composeLenses, propsLens } from '../../../../../../../Utils/lensUtils';

export interface IBusinessDataformActionExtraInfo {
  associationPath?: string;
}

export type BusinessDataFormAction<T = IFormData> =
  IBusinessDataformActionExtraInfo &
    (
      | { type: 'ENTER_EDITMODE' }
      | { type: 'LOAD_INITIAL_VALUE'; initialValue?: T }
      | {
          /**
           * Sätt loading state
           * Loading uppdateras även i LOAD_DATA om det sker i samband med att data lästs in
           */
          type: 'SET_LOADING';
          value: boolean;
        }
      | { type: 'CANCEL' }
      | { type: 'RESET' }
      | { type: 'VALUES_COMMITED'; addedId?: string }
      | {
          type: 'UPDATE';
          value: IUpdateValue;
          customValues?: any;
          validation?: IFieldValidations;
          /** If true, do not make changes to formHasChanged and do not validate */
          ignoreFormHasChanged?: boolean;
        }
      | {
          type: 'TOUCH';
          path: string[];
        }
      | {
          type: 'VALIDATE';
        }
      | {
          /**
           * Läs in data i formuläret
           * @param data Data som ska läsas in
           * @param loading Om data fortfarande laddas in
           */
          type: 'LOAD_DATA';
          loading?: boolean; //Om data fortsatt laddas in
          data: INodeData;
          valuesLens?: Lens;
          loadedValuesLens?: Lens;
        } // Läs in id och _version till values
      | {
          /**
           * @deprecated Use 'UPDATE' instead
           */
          type: 'ADD_ASSOCIATION';
          value: IUpdateValue;
          path: Array<string | number>;
          /** If true, do not make changes to formHasChanged */
          ignoreFormHasChanged?: boolean;
        }
      | {
          /**
           * @deprecated Use 'UPDATE' instead
           */
          type: 'DELETE_NEW_ASSOCIATION';
          customValues?: any;
          path: Array<string | number>;
          /** If true, do not make changes to formHasChanged */
          ignoreFormHasChanged?: boolean;
        }
      | {
          /**
           * @deprecated Use 'UPDATE' instead
           */
          type: 'DELETE_EXISTING_ASSOCIATION';
          customValues?: any;
          path: Array<string | number>;
          value: INodeData;
          /** If true, do not make changes to formHasChanged */
          ignoreFormHasChanged?: boolean;
        }
      | { type: 'DEBUG' }
    );

export interface IUpdateValue {
  [name: string]: any;
}

export interface IFormData {
  _Associations?: { [index: string]: any } | null;
  [index: string]: any;
}

export interface IAssociationFormData {
  _Associations: { [index: string]: any };
  [index: string]: any;
}

export type FieldValidationType =
  | 'Error'
  | 'Warning'
  | 'Hint'
  | 'MissingValue'
  | 'RequiredValueHint'; //hint om att fältet är om det inte framgår av modellen (används när fältet är ifyllt men en custom validering skulle gett MissingValue)

interface IFieldValidationBase {
  type: FieldValidationType;
  fieldName: string;
  path?: string[];
  message: string;
  showAsHint?: boolean;
}

interface IMissingValueFieldValidation
  extends Omit<IFieldValidationBase, 'message'> {
  type: 'MissingValue';
  message?: string;
}

interface IRequiredValueHintFieldValidation
  extends Omit<IFieldValidationBase, 'message'> {
  type: 'RequiredValueHint';
  message?: string;
}

export type IFieldValidation =
  | IFieldValidationBase
  | IMissingValueFieldValidation
  | IRequiredValueHintFieldValidation;

export type IFieldValidations = Array<IFieldValidation>;

const resetTempStates = {
  editCancelled: false,
  newObjectSaved: false,
  showError: false,
} as const;

const businessDataFormValuesMergeArrays = (
  leftArray: any[],
  rightArray: any[]
): any[] => {
  const mergedArray = rightArray.reduce<any[]>(
    (previousArray, currentElement) => {
      const previousArrayIndex = previousArray.findIndex(
        previousArrayElement => previousArrayElement.id === currentElement.id
      );

      if (previousArrayIndex === -1) {
        return append(currentElement, previousArray);
      } else {
        return update(
          previousArrayIndex,
          mergeBusinessDataFormValues(
            previousArray[previousArrayIndex],
            currentElement
          ),
          previousArray
        );
      }
    },
    leftArray
  );

  return mergedArray;
};

const businessDataFormValuesMergeFunction = (
  key: string,
  leftValue: any,
  rightValue: any
) => {
  if (type(leftValue) === 'Array' && type(rightValue) === 'Array') {
    return businessDataFormValuesMergeArrays(leftValue, rightValue);
  } else {
    return rightValue;
  }
};

export const mergeBusinessDataFormValues = (
  leftValue: IFormData,
  rightValue: IFormData
) =>
  mergeDeepWithKey(businessDataFormValuesMergeFunction, leftValue, rightValue);

/**
 * Mergar ändringar av olika typer, t. ex Update, Add, Remove till 1 sammanslaget objekt
 */
export const mergeModifications = (
  parsedAssociations: Array<[string, any]>
): { [key: string]: any } => {
  return parsedAssociations.reduce<Record<string, unknown>>((prev, curr) => {
    if (isArray(curr[1])) {
      if (prev[curr[0]] && isArray(prev[curr[0]])) {
        prev[curr[0]] = (prev[curr[0]] as unknown[]).concat(curr[1]);
      } else {
        prev[curr[0]] = curr[1];
      }
    } else {
      prev[curr[0]] = curr[1];
    }

    return prev;
  }, {});
};
/** Ersätter _Ref och _Values, för att omvandla formatet för ändringar mer likt det för inläst data */
export const parseText = (formValue: IFormData) => {
  let formValues = clone(formValue);

  let parsedAssociations: any[] = [];

  if (formValues && formValues._Associations) {
    for (let key in formValues._Associations) {
      if (Object.prototype.hasOwnProperty.call(formValues._Associations, key)) {
        const value: IFormData[] | IFormData = formValues._Associations[key];

        const newKey = key.startsWith('_') ? key : key.split('_')[0];

        let newValue;
        if (type(value) === 'Object') {
          newValue = parseText(value);
        } else if (type(value) === 'Array') {
          newValue = value.map((x: any) => parseText(x));
        } else {
          newValue = value;
        }
        parsedAssociations.push([newKey, newValue]);
      }
    }

    formValues._Associations = mergeModifications(parsedAssociations);
  }

  return formValues;
};

export const compareValues = (
  parsedFormStateValues: IFormData,
  model: IClientBusinessModel | undefined,
  fullModel: AvenyCollectionMap,
  level: number = 0,
  validationArray: IFieldValidations,
  data: INodeData | null,
  isNew: boolean
) => {
  const associations = model?.Associations;
  const attributes = model?.Attributes;

  const typ = parsedFormStateValues._typ || data?._typ;

  // Loop attributes
  for (let property in attributes) {
    if (Object.prototype.hasOwnProperty.call(attributes, property)) {
      const ValidInComps = attributes[property].ValidInComps;
      if (ValidInComps && ValidInComps.length > 0) {
        if (typ && model) {
          if (!isValidInClass(model, typ)(attributes[property])) {
            continue;
          }
        } else {
          // Error: No type
        }
      }
      const isNewPost =
        isNew ||
        (typeof parsedFormStateValues.id === 'string' &&
          parsedFormStateValues.id.startsWith('New'));
      if (attributes[property].Required) {
        if (
          ((!(data?.[attributes[property].AttributeName] === undefined) ||
            isNewPost) &&
            parsedFormStateValues[attributes[property].AttributeName] === '') ||
          parsedFormStateValues[attributes[property].AttributeName] === null
        ) {
          validationArray.push({
            fieldName: attributes[property].AttributeName,
            message: `${attributes[property].AttributeName} saknar värde`,
            type: 'MissingValue',
          });
        }
      }
    }
  }

  // Loop associations
  for (let association in associations) {
    if (Object.prototype.hasOwnProperty.call(associations, association)) {
      if (
        associations[association].ValidInComps &&
        associations[association].ValidInComps.length > 0
      ) {
        if (typ && model) {
          if (!isValidInClass(model, typ)(associations[association])) {
            continue;
          }
        } else {
          // Error: No type
        }
      }

      if (associations[association].Required) {
        if (
          ((!(
            data?._Associations?.[associations[association].AssociationName] ===
            undefined
          ) ||
            isNew) &&
            parsedFormStateValues._Associations?.[
              associations[association].AssociationName
            ] === '') ||
          parsedFormStateValues._Associations?.[
            associations[association].AssociationName
          ] === null
        ) {
          validationArray.push({
            fieldName: associations[association].AssociationName,
            message: `${associations[association].AssociationName} saknar värde`,
            type: 'MissingValue',
          });
        }
      }

      if (level < 5) {
        const associatedCollection = fullModel.get(
          associations[association].AssociatedCollectionName.toLowerCase()
        );
        const associatedParsedAssociations =
          parsedFormStateValues._Associations &&
          typeof parsedFormStateValues._Associations[association] === 'object'
            ? parsedFormStateValues._Associations[association]
            : undefined;

        const associatedDataAssociations =
          data &&
          data._Associations &&
          typeof data._Associations[association] === 'object'
            ? data._Associations[association]
            : undefined;

        if (associatedParsedAssociations) {
          if (type(associatedParsedAssociations) === 'Array') {
            associatedParsedAssociations.forEach((property: any) => {
              compareValues(
                property,
                associatedCollection,
                fullModel,
                level + 1,
                validationArray,
                associatedDataAssociations?.find(
                  (associatedParsedAssociation: any) =>
                    associatedParsedAssociation.id === property.id
                ),
                isNew
              );
            });
          } else {
            // Loop associated collections
            compareValues(
              associatedParsedAssociations,
              associatedCollection,
              fullModel,
              level + 1,
              validationArray,
              associatedDataAssociations,
              isNew
            );
          }
        }
      }
    }
  }
};

export const resolveRefs = (values: IFormData): IFormData => {
  let formValues = clone(values);

  let parsedAssociations: any[] = [];

  if (formValues && formValues._Associations) {
    for (let key in formValues._Associations) {
      if (Object.prototype.hasOwnProperty.call(formValues._Associations, key)) {
        let value: IFormData = formValues._Associations[key];

        let newValue = value;
        if (key.endsWith('_Ref') && value?.id) {
          if (isObject(value)) {
            newValue = value.id;
          } else {
            newValue = value;
          }
        } else if (isObject(value)) {
          newValue = resolveRefs(value);
        } else if (isArray(value)) {
          newValue = value.map((x: any) => resolveRefs(x));
        }
        parsedAssociations.push([key, newValue]);
      }
    }

    formValues._Associations = Object.fromEntries(parsedAssociations);
  }

  return formValues;
};
export type IValidationData<DataType> = {
  /** Aktuella värden, samma som formstate.currentValues (De flesta valideringar bör vara en funktion av currentValues) */
  currentValues: DataType;
  /** Values från formstate (visar vilka ändringar som gjorts) */
  values: IFormData;
  /** Formstate.autovalues */
  autovalues: IFormData;
  /** Användardefinierade värden */
  customValues: unknown;
  /** Inläst data (används om valideringen beror vilken data som lästes in). Saknas för nya poster */
  data: DataType | null;
  /** Vilka fält som har varit fokuserade av användaren, om en association kan vara en lista så hamnar värdena i ett objekt med id som key.
   *  Typ:
   *  {
   *    attributeName: boolean,
   *    _Associations: {
   *      associationName: { attributeName: boolean... } | { id: { attributeName: boolean... } }
   *    },
   *  }
   */
  touched: IFormData;
};
export type IValidateFn<DataType = IFormData> = (
  validationData: IValidationData<DataType>
) => IFieldValidations;

/** Kan användas för att återskapa formData parametern till en validering @deprecated*/
export const createFormData = (
  values: IFormData,
  data: IFormData | null
): IFormData =>
  mergeBusinessDataFormValues(parseText(data ?? {}), parseText(values));

const validateFields = (
  model: IClientBusinessModel,
  formStateValues: IFormData,
  fullModel: AvenyCollectionMap,
  data: INodeData | null,
  validateFns: IValidateFn<IFormData>[] | undefined,
  isNew: boolean,
  autoValues: IFormData,
  customValues: unknown,
  dataLoading: boolean,
  currentValues: IFormData,
  touched: IFormData
): IFieldValidations => {
  if (dataLoading) return [];
  let parsedAssociations = parseText(formStateValues);
  let validationErrors: IFieldValidations =
    validateFns != null
      ? validateFns.reduce<IFieldValidations>(
          (errorMessages, validateFn) =>
            errorMessages.concat(
              validateFn({
                values: formStateValues,
                autovalues: autoValues,
                customValues: customValues,
                data,
                currentValues: currentValues,
                touched: touched,
              })
            ),
          []
        )
      : [];

  compareValues(
    parsedAssociations,
    model,
    fullModel,
    0,
    validationErrors,
    data,
    isNew
  );

  return validationErrors;
};

export const deleteNewAssociation = (
  pathArray: Array<string | number>,
  prevStateValues: IFormData
): IFormData => {
  let valuesAssocRemoved: IFormData = dissocPath(pathArray, prevStateValues);
  const assocArrayPath = init(pathArray);
  const assocArray = path<any[]>(assocArrayPath, valuesAssocRemoved);
  if (assocArray && assocArray.length === 0) {
    valuesAssocRemoved = dissocPath(assocArrayPath, valuesAssocRemoved);
  }

  return valuesAssocRemoved;
};

export const deleteExistingAssociation = (
  pathArray: Array<string | number>,
  prevStateValues: IFormData,
  value: INodeData
) => {
  const oldArray: IUpdateValue[] = path(pathArray, prevStateValues) || [];
  const newArray = append(value, oldArray);
  const valuesAssocRemoved = assocPath(pathArray, newArray, prevStateValues);

  return valuesAssocRemoved;
};

const dataFormHasChanged = (
  initialValues: IFormData | INodeData | null,
  values: IFormData,
  errors?: IFieldValidations
) => {
  if (errors?.length) {
    return true;
  }
  let hasChanged = businessDataFormHasChanged(initialValues || {}, values);
  return hasChanged;
};

export const getTypedValidationErrorMessage = (
  validations: IFieldValidations | undefined,
  fieldName: string,
  type: FieldValidationType
) => {
  const fieldError = validations?.find(
    error => error.fieldName === fieldName && error.type === type
  );
  if (fieldError) {
    return fieldError.message;
  } else {
    return undefined;
  }
};

export const getFieldValidationErrorMessage = (
  validations: IFieldValidations | undefined,
  fieldName: string
) => getTypedValidationErrorMessage(validations, fieldName, 'Error');

export const getFieldValidationWarningMessage = (
  validations: IFieldValidations | undefined,
  fieldName: string
) => getTypedValidationErrorMessage(validations, fieldName, 'Warning');

export const getFieldValidationHintMessage = (
  validations: IFieldValidations | undefined,
  fieldName: string
) => getTypedValidationErrorMessage(validations, fieldName, 'Hint');

interface IFieldValidationInfo {
  error: boolean;
  warning: boolean;
  helperText: string;
}

export const getFieldValidationInfo = (
  validations: IFieldValidations | undefined,
  fieldName: string
): IFieldValidationInfo => {
  const errors = getFieldValidationErrorMessage(validations, fieldName);
  const warnings = getFieldValidationWarningMessage(validations, fieldName);
  const hints = getFieldValidationHintMessage(validations, fieldName);
  return {
    error: Boolean(errors),
    warning: !errors && Boolean(warnings),
    helperText: errors ?? warnings ?? hints ?? '',
  };
};

export const mergeLoadedAndEditedValues = (
  loaded: IFormData,
  edited: IFormData
): IFormData => {
  const result: IFormData = {};
  let key;
  loaded = loaded || {};
  edited = edited || {};

  for (key in loaded) {
    const editedKey = key.endsWith('_All')
      ? key.substring(0, key.length - 4)
      : key;
    const editedValue = edited[editedKey];
    if (has(key, loaded)) {
      if (isObject(loaded[key]) && isObject(edited[editedKey])) {
        result[key] = mergeLoadedAndEditedValues(
          loaded[key],
          edited[editedKey]
        );
      } else if (
        isObject(loaded[key]) &&
        typeof edited[editedKey] === 'string'
      ) {
        result[key] = assoc('id', edited[editedKey], loaded[key]);
      } else if (isArray(loaded[key]) && Array.isArray(editedValue)) {
        result[key] = editedValue.reduce<any[]>((prev, curr) => {
          const idx = prev.findIndex(val => val?.id === curr?.id);
          return idx === -1
            ? prev.concat(curr)
            : update(idx, mergeLoadedAndEditedValues(prev[idx], curr), prev);
        }, loaded[key]);
      } else {
        result[key] = has(editedKey, edited) ? edited[editedKey] : loaded[key];
      }
    }
  }

  for (key in edited) {
    if (has(key, edited) && !(has(key, result) || has(key + '_All', result))) {
      result[key] = edited[key];
    }
  }
  return result;
};

const getValuesToDeletePaths = (
  values: IFormData,
  path: string[] = []
): { [path: string]: { id: string }[] } => {
  let paths: { [path: string]: { id: string }[] } = {};
  Object.entries(values).forEach(([key, value]) => {
    if (key.endsWith('_Delete_Values') && isArray(value)) {
      paths[[...path, key.replace('_Delete_Values', '')].join('.')] = value;
    } else if (isObject(value)) {
      const newKey = key.startsWith('_') ? key : key.split('_')[0];
      paths = mergeDeepRight(
        paths,
        getValuesToDeletePaths(value, [...path, newKey])
      );
    }
  });

  return paths;
};

export const removeDeletedValues = (
  currentValues: IFormData,
  values: IFormData
) => {
  let newCurrentValues = currentValues;
  const valuesToDelete = getValuesToDeletePaths(values);

  forEachObjIndexed((deleteValue, key: string | number) => {
    newCurrentValues = over(
      composeLenses(
        ...String(key)
          .split('.')
          .map(k =>
            k === '_Associations'
              ? lensPath(['_Associations'])
              : propsLens(k, k + '_All')
          )
      ),
      associationValues => {
        if (Array.isArray(associationValues)) {
          return associationValues.filter(
            (associationValue: { id?: string }) =>
              !deleteValue.find(
                deleteValue => deleteValue.id === associationValue.id
              )
          );
        } else {
          return associationValues;
        }
      },
      newCurrentValues
    );
  }, valuesToDelete);

  return newCurrentValues;
};

export const formReducer =
  (
    parentFormContext: IClientBusinessModel,
    fullModel: AvenyCollectionMap,
    isNew: boolean,
    data: INodeData | null,
    validateFns: IValidateFn[] | undefined,
    directEditMode: boolean | undefined,
    customDataFormHasChanged?: (
      initialValues: IFormData | INodeData | null,
      values: IFormData
    ) => boolean,
    formStateActionTransformer?: (
      action: Readonly<BusinessDataFormAction>,
      formState: IBusinessDataFormState
    ) => Readonly<BusinessDataFormAction>
  ) =>
  (
    prevState: IBusinessDataFormState,
    actionParam: Readonly<BusinessDataFormAction>
  ): IBusinessDataFormState => {
    const action = formStateActionTransformer
      ? formStateActionTransformer(actionParam, prevState)
      : actionParam;

    let newState: IBusinessDataFormState | null = null;
    switch (action.type) {
      case 'ENTER_EDITMODE':
        if (prevState.editMode) {
          return prevState;
        }
        newState = { ...prevState, editMode: true };
        break;
      case 'LOAD_INITIAL_VALUE':
        newState = {
          ...prevState,
          editMode: true,
          formHasChanged: true,
          initialValues: action.initialValue ?? {},
          values: action.initialValue ?? {},
          autoValues: action.initialValue ?? {},
        };
        break;
      case 'SET_LOADING':
        newState = {
          ...prevState,
          loading: action.value,
        };
        break;
      case 'CANCEL':
        newState = {
          ...prevState,
          ...resetTempStates,
          editMode: !!directEditMode,
          editCancelled: true,
          customValues: undefined,
          values: {},
          touched: {},
          formHasChanged: false,
        };
        break;
      case 'RESET':
        newState = {
          ...prevState,
          ...resetTempStates,
          editMode: directEditMode ? true : false,
          values: {},
          touched: {},
          customValues: undefined,
          formHasChanged: false,
        };
        break;
      case 'VALUES_COMMITED':
        newState = {
          ...prevState,
          ...resetTempStates,
          editMode: directEditMode ? true : false,
          values: {},
          committedValues: prevState.values,
          touched: {},
          autoValues: {},
          commitVersion: (prevState.commitVersion ?? 0) + 1,
          addedId: action.addedId,
          formHasChanged: false,
        };
        break;
      case 'UPDATE':
        {
          const newValues = mergeBusinessDataMutations(
            prevState.values,
            action.value
          );

          const newAutoValues = action.ignoreFormHasChanged
            ? mergeBusinessDataMutations(prevState.autoValues, action.value)
            : prevState.autoValues;

          const formHasChanged = action.ignoreFormHasChanged
            ? prevState.formHasChanged
            : isNew
            ? true
            : customDataFormHasChanged
            ? customDataFormHasChanged(data, newValues)
            : dataFormHasChanged(data, newValues, action.validation);

          newState = {
            ...prevState,
            ...resetTempStates,
            values: newValues,
            customValues: action.customValues,
            autoValues: newAutoValues,
            formHasChanged,
          };
        }
        break;
      case 'TOUCH':
        {
          newState = {
            ...prevState,
            ...resetTempStates,
            touched: path(action.path, prevState.touched)
              ? prevState.touched
              : assocPath(action.path, true, prevState.touched ?? {}),
          };
        }
        break;
      case 'VALIDATE':
        {
          newState = {
            ...prevState,
            ...resetTempStates,
          };
        }
        break;
      case 'LOAD_DATA':
        {
          let values = action.valuesLens
            ? over(
                action.valuesLens,
                current => ({
                  ...current,
                  id: action.data?.id,
                  _version: action.data?._version,
                }),
                prevState.values
              )
            : {
                ...prevState.values,
                id: action.data?.id,
                _version: action.data?._version,
              };

          const loadedValues = action.loadedValuesLens
            ? set(action.loadedValuesLens, action.data, prevState.loadedValues)
            : action.data;
          newState = {
            ...prevState,
            ...resetTempStates,
            values: values,
            committedValues: undefined, //När data laddats in så ska committedValues hamnat i loadedValues (med eventuella ändringar från backend)
            loadedValues: loadedValues,
            formHasChanged: false,
            loading: !!action.loading,
          };
        }
        break;
      case 'ADD_ASSOCIATION':
        {
          const oldArray: IUpdateValue[] =
            path(action.path, prevState.values) || [];
          const newArray = append(action.value, oldArray);
          const updatedValues = assocPath(
            action.path,
            newArray,
            prevState.values
          );

          const formHasChanged = action.ignoreFormHasChanged
            ? prevState.formHasChanged
            : isNew
            ? dataFormHasChanged(
                parseText(prevState.initialValues || {}),
                updatedValues
              )
            : dataFormHasChanged(data, updatedValues);

          newState = {
            ...prevState,
            ...resetTempStates,
            values: updatedValues,
            formHasChanged,
          };
        }
        break;
      case 'DELETE_NEW_ASSOCIATION':
        {
          const valuesNewAssocRemoved = deleteNewAssociation(
            action.path,
            prevState.values
          );

          const newAutoValues = action.ignoreFormHasChanged
            ? valuesNewAssocRemoved
            : prevState.autoValues;

          const formHasChanged = action.ignoreFormHasChanged
            ? prevState.formHasChanged
            : isNew
            ? dataFormHasChanged(
                parseText(prevState.initialValues || {}),
                valuesNewAssocRemoved
              )
            : dataFormHasChanged(data, valuesNewAssocRemoved);

          newState = {
            ...prevState,
            ...resetTempStates,
            autoValues: newAutoValues,
            customValues: action.customValues,
            values: valuesNewAssocRemoved,
            formHasChanged,
          };
        }
        break;
      case 'DELETE_EXISTING_ASSOCIATION': {
        const valuesExistingAssocRemoved = deleteExistingAssociation(
          action.path,
          prevState.values,
          action.value
        );

        const formHasChanged = action.ignoreFormHasChanged
          ? prevState.formHasChanged
          : isNew
          ? dataFormHasChanged(
              parseText(prevState.initialValues || {}),
              valuesExistingAssocRemoved
            )
          : dataFormHasChanged(data, valuesExistingAssocRemoved);

        newState = {
          ...prevState,
          ...resetTempStates,
          customValues: action.customValues,
          values: valuesExistingAssocRemoved,
          formHasChanged,
        };
        break;
      }
      case 'DEBUG':
        return prevState;
        break;
    }

    //Hantera beräknade värden

    //CurrentValues beräknas utifrån committedValues om det finns, annars values
    const currentNewValues = newState.committedValues ?? newState.values;
    const currentOldValues = prevState.committedValues ?? prevState.values;

    const keepCurrentValues: boolean =
      !!prevState.currentValues &&
      currentNewValues === currentOldValues &&
      newState.loadedValues === prevState.loadedValues;

    const newCurrentValues: IFormData =
      keepCurrentValues && prevState.currentValues
        ? prevState.currentValues
        : removeDeletedValues(
            mergeLoadedAndEditedValues(
              newState.loadedValues ?? {},
              parseText(currentNewValues)
            ),
            newState.values
          );

    const makeNewValidationErrors: boolean = !newState.committedValues;

    const newValidation =
      !prevState.validation || makeNewValidationErrors
        ? validateFields(
            parentFormContext,
            newState.values,
            fullModel,
            data,
            validateFns,
            isNew ?? false,
            newState.autoValues,
            newState.customValues,
            newState.loading,
            newCurrentValues,
            newState.touched
          ).concat(
            action.type === 'UPDATE' && action.validation
              ? action.validation
              : []
          )
        : prevState.validation;

    return {
      ...newState,
      currentValues: newCurrentValues,
      validation: newValidation,
    };
  };

/** Gör en action för en businessdataform som används för att hantera ett associerat objekt (0-1 association) till en action som kan appliceras på ett överliggande state */
export const associatedAction =
  (
    associationPath: string,
    associatedId: string | undefined,
    parentData: any
  ) =>
  (action: Readonly<BusinessDataFormAction>): BusinessDataFormAction | null => {
    const associationPathParts = associationPath.split('.');
    switch (action.type) {
      case 'UPDATE': {
        const values = {
          _Associations: {
            [`${last(associationPathParts)}_Values`]: action.value,
          },
        };
        const addValue = {
          _Associations: {
            [`${last(associationPathParts)}_Add_Values`]: [
              { ...action.value, id: associatedId },
            ],
          },
        };
        const updateValue = {
          _Associations: {
            [`${last(associationPathParts)}_Update_Values`]: [
              { ...action.value, id: associatedId },
            ],
          },
        };
        const baseValue =
          associatedId == undefined
            ? values
            : associatedId.startsWith('New')
            ? addValue
            : updateValue;
        const value = init(associationPathParts).reduceRight<IUpdateValue>(
          (prev, curr, index) => ({
            _Associations: {
              [`${curr}_Values`]: {
                ...getAssociationIdVersion(
                  associationPathParts,
                  associationPathParts.length - index - 2,
                  parentData
                ),
                ...prev,
              },
            },
          }),
          baseValue
        );
        return {
          type: 'UPDATE',
          value: value,
        };
      }
      case 'TOUCH': {
        const path = [
          '_Associations',
          ...intersperse('_Associations', associationPath.split('.')),
          ...(associatedId ? [associatedId] : []),
        ].concat(action.path);

        return { type: 'TOUCH', path };
      }
      case 'LOAD_DATA':
        return null;
      default:
        return {
          ...action,
          associationPath: `${action.associationPath}.${associationPath}`,
        };
    }
  };

export const removeIdsFromNewAssociations = (values: IFormData): IFormData => {
  let newFormData: IFormData = values;

  Object.entries(values).forEach(([key, value]) => {
    if (key.includes('_Add_Values')) {
      newFormData = assoc(
        key,
        (value as any[]).map(addValue => {
          const withoutId = omit(['id'], addValue);

          return removeIdsFromNewAssociations(withoutId);
        }),
        newFormData
      );
    } else if (type(value) === 'Object') {
      newFormData = assoc(
        key,
        removeIdsFromNewAssociations(value),
        newFormData
      );
    } else if (type(value) === 'Array') {
      newFormData = assoc(
        key,
        (value as any[]).map(addValue =>
          removeIdsFromNewAssociations(addValue)
        ),
        newFormData
      );
    }
  });

  return newFormData;
};

export const removeIdVersion = (values: IFormData): IFormData => {
  let newFormData = omit(['id', '_version'], values);
  Object.entries(values).forEach(([key, value]) => {
    if (type(value) === 'Object') {
      newFormData = assoc(key, removeIdVersion(value), newFormData);
    } else if (type(value) === 'Array') {
      newFormData = assoc(
        key,
        (value as any[]).map(addValue => removeIdVersion(addValue)),
        newFormData
      );
    }
  });
  return newFormData;
};

export interface IEditMenuActionParams {
  businessDataFormContext: IDefinedBusinessDataFormContext<any>;
  selectDetail?: (id: TSelectedDetail, force: boolean) => void;
  editMenuOnCancel: (() => void) | undefined;
}
export type DisabledType =
  | JSX.Element[]
  | JSX.Element
  | boolean
  | string
  | undefined;

export interface IEditMenuAction {
  /** Unique id string used by react as key when rendering */
  id: string;
  /** Label of the button */
  label:
    | string
    | ((editMenuActionParams: IEditMenuActionParams) => string | JSX.Element)
    | JSX.Element;
  shortLabel?:
    | string
    | ((editMenuActionParams: IEditMenuActionParams) => string);
  onClick: (editMenuActionParams: IEditMenuActionParams) => void;
  isHidden?: (editMenuActionParams: IEditMenuActionParams) => boolean;
  isDisabled?: (editMenuActionParams: IEditMenuActionParams) => DisabledType;
  primary?: boolean;
  flat?: boolean | ((editMenuActionParams: IEditMenuActionParams) => boolean);
  icon: IconType;
  /** This will hide the icon if the button is not displayed in a floating menu */
  hideIconIfNotFloating?: boolean;
  confirmButtonCaption?:
    | string
    | ((editMenuActionParams: IEditMenuActionParams) => string);
  confirmDialogCaption?:
    | string
    | ((editMenuActionParams: IEditMenuActionParams) => string);
  confirmDialogMessage?:
    | string
    | JSX.Element
    | ((editMenuActionParams: IEditMenuActionParams) => string | JSX.Element);
}

export const getEditMenuSaveButtonDisabled = (
  validationErrors: IFieldValidations,
  formHasChanged: boolean
): DisabledType => {
  let tooltip = '';

  let requiredFieldsMissingValue = validationErrors.find(
    validation => validation.type === 'MissingValue'
  );

  const fieldErrorMessages = validationErrors
    .filter(error => error.type === 'Error')
    .map(fieldError => fieldError.message);

  if (!formHasChanged) {
    tooltip = 'Inga ändringar gjorda';
  }

  if (formHasChanged && requiredFieldsMissingValue) {
    tooltip = 'Fyll i alla obligatoriska fält för att spara';
  }

  const validationErrorMessages = tooltip
    ? [tooltip, ...fieldErrorMessages]
    : fieldErrorMessages;

  if (validationErrorMessages.length === 0) {
    return false;
  }

  return validationErrorMessages.map(message => (
    <div key={message}>{message}</div>
  ));
};

export const defaultEditMenuCancelButton: IEditMenuAction = {
  id: 'DefaultAvbryt',
  label: ({ businessDataFormContext }) =>
    businessDataFormContext.directEditMode ? 'Ångra ändringar' : 'Avbryt',
  shortLabel: ({ businessDataFormContext }) =>
    businessDataFormContext.directEditMode ? 'Ångra' : 'Avbryt',
  onClick: ({ businessDataFormContext, editMenuOnCancel }) => {
    businessDataFormContext?.formStateDispatch({ type: 'CANCEL' });

    editMenuOnCancel?.();
  },
  primary: true,
  flat: true,
  isDisabled: ({ businessDataFormContext }) =>
    businessDataFormContext.directEditMode
      ? businessDataFormContext.mutationLoading ||
        !businessDataFormContext.formState.formHasChanged
      : businessDataFormContext.mutationLoading,
  icon: 'undo',
  hideIconIfNotFloating: true,
};

export const defaultEditMenuSaveButton: IEditMenuAction = {
  id: 'DefaultSpara',
  label: ({ businessDataFormContext }) =>
    businessDataFormContext.mutationLoading ? 'Sparar' : 'Spara',
  onClick: ({ businessDataFormContext }) => {
    businessDataFormContext.saveForm();
  },
  primary: true,
  icon: 'save_alt',
  flat: false,
  isDisabled: ({ businessDataFormContext }) => {
    if (businessDataFormContext.mutationLoading) {
      return true;
    }
    if (businessDataFormContext.dataLoading) {
      return true;
    }

    const disabledReason = getEditMenuSaveButtonDisabled(
      businessDataFormContext.formState.validation,
      businessDataFormContext.formState.formHasChanged
    );

    return disabledReason;
  },
};

export const defaultEditMenuSaveAndCloseButton: IEditMenuAction = {
  id: 'DefaultSparaOchStang',
  label: ({ businessDataFormContext }) =>
    businessDataFormContext.mutationLoading ? 'Sparar' : 'Spara och stäng',
  onClick: ({ businessDataFormContext, selectDetail }) => {
    businessDataFormContext.saveForm(
      undefined,
      () => {
        selectDetail?.(null, true);
      },
      undefined,
      { suppressAddedIdSelection: true }
    );
  },
  primary: true,
  icon: 'check_circle_outline',
  flat: false,
  isDisabled: ({ businessDataFormContext }) => {
    if (businessDataFormContext.mutationLoading) {
      return true;
    }
    if (businessDataFormContext.dataLoading) {
      return true;
    }

    const disabledReason = getEditMenuSaveButtonDisabled(
      businessDataFormContext.formState.validation,
      businessDataFormContext.formState.formHasChanged
    );

    return disabledReason;
  },
};

interface ISplitEditMenuActions {
  directActions?: IEditMenuAction[];
  otherActions?: IEditMenuAction[];
}

export const splitEditMenuActions = (
  actions: IEditMenuAction[],
  maxNumberOfDirectActions: number
): ISplitEditMenuActions => {
  if (actions.length >= maxNumberOfDirectActions + 1) {
    return {
      directActions: takeLast(maxNumberOfDirectActions, actions),
      otherActions: take(actions.length - maxNumberOfDirectActions, actions),
    };
  } else {
    return { directActions: actions };
  }
};
